// src/pages/Feedback.jsx
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useUser, useAuth } from "@clerk/clerk-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import {
  ArrowLeft,
  Send,
  CheckCircle,
  AlertCircle,
  MessageSquare,
  Settings,
  Loader2,
} from "lucide-react";

const API_BASE =
  (typeof import.meta !== "undefined" &&
    import.meta.env?.VITE_API_BASE?.replace(/\/+$/, "")) ||
  (process.env.REACT_APP_API_BASE &&
    process.env.REACT_APP_API_BASE.replace(/\/+$/, "")) ||
  "http://localhost:5050";

async function fetchJSON(path, { token, method = "GET", body } = {}) {
  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    credentials: "include",
    body: body ? JSON.stringify(body) : undefined,
  });
  const isJson = res.headers.get("content-type")?.includes("application/json");
  const json = isJson ? await res.json().catch(() => ({})) : {};
  if (!res.ok) throw new Error(json?.error || `HTTP ${res.status}`);
  return json?.data ?? json;
}

function safeTrim(v) {
  return String(v || "").trim();
}

export default function Feedback() {
  const navigate = useNavigate();
  const { user, isLoaded } = useUser();
  const { getToken } = useAuth();

  const [formData, setFormData] = useState({
    category: "general",
    subject: "",
    message: "",
  });

  const [me, setMe] = useState(null); // optional extra context
  const [loadingMe, setLoadingMe] = useState(true);

  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const identity = useMemo(() => {
    const fullName =
      user?.fullName ||
      [user?.firstName, user?.lastName].filter(Boolean).join(" ") ||
      "";
    const email = user?.primaryEmailAddress?.emailAddress || "";
    return { fullName: safeTrim(fullName), email: safeTrim(email) };
  }, [user]);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setLoadingMe(true);
        const token = await getToken().catch(() => null);
        // Optional: enrich feedback with church/scope/instructor
        const data = await fetchJSON("/api/users/me", { token }).catch(() => null);
        if (alive) setMe(data || null);
      } finally {
        if (alive) setLoadingMe(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [getToken]);

  const handleChange = (e) => {
    setFormData((p) => ({ ...p, [e.target.name]: e.target.value }));
  };

  const validate = () => {
    if (!isLoaded) return "Loading your account…";
    if (!identity.email) return "Your account email is missing. Please update it in Profile.";
    if (!safeTrim(formData.subject)) return "Please add a subject.";
    if (!safeTrim(formData.message)) return "Please write your message.";
    if (safeTrim(formData.message).length > 500) return "Message is too long (max 500 characters).";
    return "";
  };

  const storeFallback = (payload) => {
    const existing = JSON.parse(localStorage.getItem("feedbackSubmissions") || "[]");
    existing.push(payload);
    localStorage.setItem("feedbackSubmissions", JSON.stringify(existing));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    const msg = validate();
    if (msg) {
      setError(msg);
      return;
    }

    const payload = {
      id: Date.now(),
      timestamp: new Date().toISOString(),
      category: formData.category,
      subject: safeTrim(formData.subject),
      message: safeTrim(formData.message),
      // identity from Clerk (since this is logged-in only)
      user: {
        full_name: identity.fullName || null,
        email: identity.email || null,
        clerk_id: user?.id || null,
      },
      // extra context (optional)
      context: {
        page: typeof window !== "undefined" ? window.location.pathname : null,
        user_agent: typeof navigator !== "undefined" ? navigator.userAgent : null,
        church_id: me?.church_id ?? me?.scope?.churchId ?? null,
        church_name: me?.church_name ?? me?.churchName ?? me?.church ?? null,
        instructor_name: me?.instructor_name ?? me?.instructorName ?? me?.instructor ?? null,
        role: me?.role ?? null,
      },
    };

    setSubmitting(true);
    try {
      const token = await getToken().catch(() => null);

      // ✅ Recommended: send to backend when endpoint exists
      // Create this later: POST /api/feedback  (store in DB + notify admins)
      // await fetchJSON("/api/feedback", { token, method: "POST", body: payload });

      // Temporary fallback (keeps it working now)
      storeFallback(payload);

      setSubmitted(true);

      // auto-return after a bit
      setTimeout(() => navigate(-1), 2500);
    } catch (err) {
      console.error("Feedback submit error:", err);
      setError(err?.message || "Failed to submit feedback. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen flex flex-col bg-gradient-to-br from-slate-50 to-white dark:from-slate-950 dark:to-slate-900">
        <Navbar />

        <main className="flex-1 pt-24 px-4 sm:px-6 lg:px-8">
          <div className="max-w-lg mx-auto">
            <div className="rounded-3xl border border-slate-200 bg-white/90 backdrop-blur shadow-2xl p-8 text-center dark:bg-slate-950/60 dark:border-slate-800">
              <div className="mx-auto mb-4 h-14 w-14 rounded-2xl bg-gradient-to-r from-emerald-500 to-green-500 flex items-center justify-center shadow-lg">
                <CheckCircle size={28} className="text-white" />
              </div>

              <h2 className="text-2xl font-extrabold text-slate-900 dark:text-white">
                Thank you!
              </h2>
              <p className="text-slate-600 dark:text-slate-300 mt-2">
                Your feedback has been submitted. We’ll review it and respond if needed.
              </p>

              <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
                <button
                  onClick={() => navigate(-1)}
                  className="inline-flex items-center justify-center rounded-2xl bg-slate-900 px-5 py-3 font-bold text-white hover:opacity-90 transition dark:bg-white dark:text-slate-900"
                >
                  Go back now
                </button>

                <button
                  onClick={() => navigate("/learner")}
                  className="inline-flex items-center justify-center rounded-2xl border border-slate-200 bg-white px-5 py-3 font-bold text-slate-800 hover:bg-slate-50 transition dark:bg-slate-900 dark:border-slate-700 dark:text-white dark:hover:bg-slate-800"
                >
                  Back to Dashboard
                </button>
              </div>

              <p className="mt-4 text-xs text-slate-500 dark:text-slate-400">
                Redirecting…
              </p>
            </div>
          </div>
        </main>

        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-slate-50 to-white dark:from-slate-950 dark:to-slate-900">
      <Navbar />

      <main className="flex-1 pt-24 px-4 sm:px-6 lg:px-8 pb-10">
        {/* Soft blobs */}
        <div className="pointer-events-none absolute inset-0 overflow-hidden">
          <div className="absolute -top-10 -left-10 h-72 w-72 rounded-full bg-orange-300/25 blur-3xl dark:bg-orange-500/10" />
          <div className="absolute top-10 -right-10 h-72 w-72 rounded-full bg-amber-300/25 blur-3xl dark:bg-amber-500/10" />
          <div className="absolute -bottom-16 left-16 h-72 w-72 rounded-full bg-orange-200/25 blur-3xl dark:bg-orange-400/10" />
        </div>

        <div className="relative max-w-4xl mx-auto">
          <button
            onClick={() => navigate(-1)}
            className="group inline-flex items-center gap-2 text-slate-600 hover:text-orange-700 dark:text-slate-300 dark:hover:text-orange-200 transition mb-6"
          >
            <ArrowLeft size={18} className="group-hover:-translate-x-1 transition-transform" />
            <span className="font-semibold">Back</span>
          </button>

          <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white/90 backdrop-blur shadow-2xl dark:bg-slate-950/60 dark:border-slate-800">
            {/* Header */}
            <div className="p-6 sm:p-8 bg-gradient-to-r from-orange-600 to-amber-600 text-white">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="flex items-center gap-3">
                    <div className="h-12 w-12 rounded-2xl bg-white/15 flex items-center justify-center">
                      <MessageSquare size={26} />
                    </div>
                    <h1 className="text-2xl sm:text-3xl font-extrabold">
                      Feedback & Support
                    </h1>
                  </div>
                  <p className="text-white/90 mt-2 max-w-2xl">
                    Share suggestions, report issues, or ask questions — we’ll help you fast.
                  </p>

                  {/* Logged-in identity (subtle, not editable) */}
                  <div className="mt-4 flex flex-wrap gap-2 text-sm">
                    <span className="inline-flex items-center gap-2 rounded-2xl bg-white/15 px-3 py-1.5 font-semibold">
                      {isLoaded ? (identity.fullName || "Learner") : "Loading account…"}
                    </span>
                    <span className="inline-flex items-center gap-2 rounded-2xl bg-white/15 px-3 py-1.5 font-semibold">
                      {isLoaded ? (identity.email || "—") : "—"}
                    </span>
                    {loadingMe ? null : me?.church_name || me?.churchName || me?.church ? (
                      <span className="inline-flex items-center gap-2 rounded-2xl bg-white/15 px-3 py-1.5 font-semibold">
                        Church:{" "}
                        <span className="font-extrabold">
                          {me?.church_name || me?.churchName || me?.church}
                        </span>
                      </span>
                    ) : null}
                  </div>
                </div>

                {submitting ? (
                  <div className="hidden sm:inline-flex items-center gap-2 rounded-2xl bg-white/15 px-3 py-2 text-sm font-bold">
                    <Loader2 className="animate-spin" size={18} />
                    Sending…
                  </div>
                ) : null}
              </div>
            </div>

            <form onSubmit={handleSubmit} className="p-6 sm:p-8 space-y-6">
              {error && (
                <div className="flex items-start gap-2 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-200">
                  <AlertCircle size={18} className="mt-0.5" />
                  <div className="font-semibold">{error}</div>
                </div>
              )}

              {/* Category */}
              <div>
                <label className="block text-slate-800 dark:text-slate-200 font-extrabold mb-3">
                  Category <span className="text-red-500">*</span>
                </label>

                <div className="grid sm:grid-cols-2 gap-4">
                  {/* Technical */}
                  <label
                    className={`flex items-start gap-3 p-4 border rounded-2xl cursor-pointer transition ${
                      formData.category === "technical"
                        ? "border-orange-500 bg-orange-50 shadow-sm dark:bg-orange-500/10 dark:border-orange-500/40"
                        : "border-slate-200 bg-white hover:border-orange-300 hover:bg-orange-50/50 dark:bg-slate-950/40 dark:border-slate-800 dark:hover:border-orange-500/30"
                    }`}
                  >
                    <input
                      type="radio"
                      name="category"
                      value="technical"
                      checked={formData.category === "technical"}
                      onChange={handleChange}
                      className="mt-1 h-5 w-5 text-orange-600 focus:ring-orange-500"
                    />
                    <div className="flex items-start gap-3">
                      <div className="h-10 w-10 rounded-2xl bg-white border border-orange-100 flex items-center justify-center dark:bg-slate-900 dark:border-slate-800">
                        <Settings className="text-orange-700 dark:text-orange-200" size={20} />
                      </div>
                      <div>
                        <p className="font-extrabold text-slate-900 dark:text-white">
                          Technical issue
                        </p>
                        <p className="text-sm text-slate-600 dark:text-slate-300">
                          Something not working as expected.
                        </p>
                      </div>
                    </div>
                  </label>

                  {/* General */}
                  <label
                    className={`flex items-start gap-3 p-4 border rounded-2xl cursor-pointer transition ${
                      formData.category === "general"
                        ? "border-orange-500 bg-orange-50 shadow-sm dark:bg-orange-500/10 dark:border-orange-500/40"
                        : "border-slate-200 bg-white hover:border-orange-300 hover:bg-orange-50/50 dark:bg-slate-950/40 dark:border-slate-800 dark:hover:border-orange-500/30"
                    }`}
                  >
                    <input
                      type="radio"
                      name="category"
                      value="general"
                      checked={formData.category === "general"}
                      onChange={handleChange}
                      className="mt-1 h-5 w-5 text-orange-600 focus:ring-orange-500"
                    />
                    <div className="flex items-start gap-3">
                      <div className="h-10 w-10 rounded-2xl bg-white border border-orange-100 flex items-center justify-center dark:bg-slate-900 dark:border-slate-800">
                        <MessageSquare className="text-orange-700 dark:text-orange-200" size={20} />
                      </div>
                      <div>
                        <p className="font-extrabold text-slate-900 dark:text-white">
                          General feedback
                        </p>
                        <p className="text-sm text-slate-600 dark:text-slate-300">
                          Suggestions, questions, and ideas.
                        </p>
                      </div>
                    </div>
                  </label>
                </div>
              </div>

              {/* Subject */}
              <div>
                <label className="block text-slate-800 dark:text-slate-200 font-extrabold mb-2">
                  Subject <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  placeholder="Brief summary of your feedback"
                  className="w-full px-4 py-3 rounded-2xl border border-slate-200 bg-white text-slate-900 placeholder:text-slate-400 focus:border-orange-400 focus:ring-4 focus:ring-orange-100 outline-none transition dark:bg-slate-900 dark:border-slate-700 dark:text-white dark:placeholder:text-slate-500 dark:focus:ring-orange-500/10"
                  maxLength={120}
                  required
                />
              </div>

              {/* Message */}
              <div>
                <label className="block text-slate-800 dark:text-slate-200 font-extrabold mb-2">
                  Message <span className="text-red-500">*</span>
                </label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Please provide details…"
                  rows={6}
                  className="w-full px-4 py-3 rounded-2xl border border-slate-200 bg-white text-slate-900 placeholder:text-slate-400 focus:border-orange-400 focus:ring-4 focus:ring-orange-100 outline-none transition resize-none dark:bg-slate-900 dark:border-slate-700 dark:text-white dark:placeholder:text-slate-500 dark:focus:ring-orange-500/10"
                  maxLength={500}
                  required
                />
                <div className="mt-2 flex items-center justify-between text-xs text-slate-500 dark:text-slate-400">
                  <span>{safeTrim(formData.message).length} / 500</span>
                  {!isLoaded ? <span className="text-orange-700 dark:text-orange-200 font-semibold">Loading account…</span> : null}
                </div>
              </div>

              {/* Actions */}
              <div className="pt-2 flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-3">
                <button
                  type="button"
                  onClick={() => navigate(-1)}
                  className="inline-flex items-center justify-center rounded-2xl border border-slate-200 bg-white px-5 py-3 font-extrabold text-slate-800 hover:bg-slate-50 transition dark:bg-slate-900 dark:border-slate-700 dark:text-white dark:hover:bg-slate-800"
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  disabled={submitting}
                  className="inline-flex items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-orange-600 to-amber-600 px-6 py-3 font-extrabold text-white shadow-lg hover:opacity-95 transition disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {submitting ? <Loader2 className="animate-spin" size={18} /> : <Send size={18} />}
                  {submitting ? "Submitting…" : "Submit feedback"}
                </button>
              </div>

              <div className="pt-2 text-sm text-slate-600 dark:text-slate-300">
                Need urgent support? Email{" "}
                <a
                  href="mailto:support@vopvirtualschool.org"
                  className="font-extrabold text-orange-700 hover:underline dark:text-orange-200"
                >
                  support@vopvirtualschool.org
                </a>
                .
              </div>
            </form>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
